/**
 * Developer mode — the gate for workflow/graph editing.
 *
 * Product rule: the SOP is the source of truth, and the generated Workflow and Graph are
 * projections of it. Developer mode was the escape hatch that revealed the workflow/graph
 * editors and the regeneration controls to super-admins only.
 *
 * **The gate is currently open.** `canEditInternals` returns true for everyone, so anyone
 * who can view a workflow can also open its editor. Opened deliberately and temporarily,
 * at the point the gate is read rather than at each of its four call sites, so the
 * branching structure survives and restoring the rule is a one-line change here:
 *
 *     canEditInternals: isSuperAdmin && isDeveloperMode
 *
 * The toggle itself still works and Settings → Developer still shows it — it just no
 * longer decides anything. Note what this means in practice: a workflow edited by hand is
 * still a derived artifact, so the next generation overwrites it. The UI says so.
 */

import { useAtom } from 'jotai';
import { developerModeAtom } from '@/stores/ui.store';
import { usePermissions } from './use-permissions';

export interface DeveloperMode {
	/** Raw toggle state (what the Settings switch reflects). */
	isDeveloperMode: boolean;
	/** True when this user is allowed to see/flip the toggle at all. */
	canUseDeveloperMode: boolean;
	/**
	 * Branch on this to reveal workflow/graph editing + regeneration.
	 * Currently always true — see the note at the top of this file.
	 */
	canEditInternals: boolean;
	setDeveloperMode: (enabled: boolean) => void;
}

export function useDeveloperMode(): DeveloperMode {
	const [isDeveloperMode, setDeveloperMode] = useAtom(developerModeAtom);
	const { isSuperAdmin } = usePermissions();

	return {
		isDeveloperMode,
		canUseDeveloperMode: isSuperAdmin,
		// Gate temporarily open: restore `isSuperAdmin && isDeveloperMode` to close it.
		canEditInternals: true,
		setDeveloperMode
	};
}

/**
 * Read-only variant for components that only need the gate.
 *
 * Kept as a hook rather than inlined `true` so the call sites stay honest about depending
 * on a gate, and closing it again touches only this file.
 */
export function useCanEditInternals(): boolean {
	// Gate temporarily open: restore `isSuperAdmin && isDeveloperMode` to close it.
	return true;
}
